import { resolveLabel } from "@esgt/helpers"
import {
  KEYED_DP_SEPARATOR,
  type KpiBreakdown,
  type KpiBreakdownFallback,
  type KpiBreakdownSegment,
  type KpiBreakdownValuePrefix,
  type RatingDatapoints,
} from "@esgt/types"

export interface BreakdownRow {
  key: string
  /** One resolved label per suffix segment (e.g. ["Svoveldioksid", "Luft"]) */
  columns: string[]
  /** One formatted value per value column (one per entry in `valuePrefixes`). */
  values: string[]
}

/**
 * Extracts breakdown rows from flat datapoints using convention-based
 * key patterns and one or more value prefixes.
 *
 * Discovers the union of suffix keys across all prefixes, producing one
 * row per unique suffix with a value per prefix.
 *
 * When the keyed-datapoint scan yields no rows and the breakdown has a
 * `fallback` configured (with an optional condition), the fallback rows
 * are returned instead.
 *
 * Auto-sum datapoints (keys ending in `_autosum`) are excluded.
 *
 * Returns `null` when no keyed datapoints are found **and** no fallback
 * is configured — callers can use this to skip rendering entirely.
 *
 * Example:
 *   valuePrefixes: [{ datapointPrefix: "hazWasteRecycled" }, { datapointPrefix: "hazWasteDisposed" }]
 *   suffixSegments: [{ header: "Unit" }, { header: "Waste type" }]
 *   datapoints: { hazWasteRecycled_kg_Batteri: 26, hazWasteDisposed_kg_Batteri: 4 }
 *
 * Returns:
 *   [{ key: "kg_Batteri", columns: ["kg", "Batteri"], values: ["26", "4"] }]
 */
export function getBreakdownRows(datapoints: RatingDatapoints, breakdown: KpiBreakdown): BreakdownRow[] | null {
  const { suffixSegments, valuePrefixes, fallback } = breakdown
  const rows = scanKeyedDatapoints(datapoints, suffixSegments, valuePrefixes)

  if (rows.length > 0) return rows

  // No keyed datapoints found — try fallback if configured.
  if (fallback) {
    return resolveFallbackRows(datapoints, suffixSegments, fallback)
  }

  return null
}

/**
 * Scans flat datapoints for convention-keyed entries matching the given
 * value prefixes and returns one row per unique suffix.
 */
function scanKeyedDatapoints(
  datapoints: RatingDatapoints,
  suffixSegments: KpiBreakdownSegment[],
  valuePrefixes: KpiBreakdownValuePrefix[],
): BreakdownRow[] {
  const rowMap = new Map<string, string[]>()

  for (let prefixIdx = 0; prefixIdx < valuePrefixes.length; prefixIdx++) {
    const prefix = `${valuePrefixes[prefixIdx].datapointPrefix}${KEYED_DP_SEPARATOR}`

    for (const [key, value] of Object.entries(datapoints)) {
      if (!key.startsWith(prefix) || key.endsWith(`${KEYED_DP_SEPARATOR}autosum`)) continue

      const suffix = key.slice(prefix.length)

      if (!rowMap.has(suffix)) {
        rowMap.set(suffix, new Array(valuePrefixes.length).fill("-"))
      }

      const row = rowMap.get(suffix)
      if (row) {
        row[prefixIdx] = value?.toString() ?? "-"
      }
    }
  }

  return [...rowMap.entries()]
    .map(([suffix, values]) => {
      const parts = suffix.split(KEYED_DP_SEPARATOR)
      const columns = suffixSegments.map((seg, i) => resolveLabel(seg.selectSource, parts[i] ?? ""))

      return { key: suffix, columns, values }
    })
    .sort((a, b) => {
      for (let i = 0; i < a.columns.length; i++) {
        const cmp = (a.columns[i] ?? "").localeCompare(b.columns[i] ?? "")
        if (cmp !== 0) return cmp
      }
      return 0
    })
}

/**
 * Produces fallback rows for a breakdown KPI when the normal keyed-datapoint
 * scan returns no results.
 *
 * Returns the fallback rows only when:
 * 1. A `fallback` config is provided, AND
 * 2. The optional condition is satisfied (or no condition is set).
 *
 * Each fallback row reads its values from named datapoints rather than from
 * convention-keyed prefixed datapoints.
 */
function resolveFallbackRows(
  datapoints: RatingDatapoints,
  suffixSegments: KpiBreakdownSegment[],
  fallback: KpiBreakdownFallback,
): BreakdownRow[] {
  // Check the optional condition.
  if (fallback.condition) {
    const actual = datapoints[fallback.condition.datapoint]
    if (actual === undefined || Number(actual) !== fallback.condition.equals) {
      return []
    }
  }

  return fallback.rows.map((row) => {
    const key = row.suffixValues.join(KEYED_DP_SEPARATOR)
    const columns = suffixSegments.map((seg, i) => resolveLabel(seg.selectSource, row.suffixValues[i] ?? ""))
    const values = row.valueDatapoints.map((dpName) => {
      const v = datapoints[dpName]
      return v !== undefined ? v.toString() : "-"
    })
    return { key, columns, values }
  })
}
